import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowDown, ArrowLeft, ArrowUp, BookmarkPlus, Copy, Eye, ImagePlus, Plus, Save, Square, Circle, Trash2, Type,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { sprekersApi } from '../api';
import ConversionReportNotice from '../components/ConversionReportNotice';
import SlidePreviewDialog, { LazySlidePreview } from '../components/SlidePreview';
import ElementInspector from '../studio/ElementInspector';
import SlideCanvasEditor from '../studio/SlideCanvasEditor';
import { newImageElement, newShapeElement, newTextElement } from '../studio/slideModel';
import { moveElement } from '../studio/studioGeometry';
import { applyText } from '../studio/studioText';
import { DEFAULT_PAGE_HEIGHT } from '../types';
import type { PresentationDetail, PresentationSlide, SlideBackground, SlideElement } from '../types';

const detail = (error: any, fallback: string): string => (
  typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : fallback
);

/**
 * Zoea Studio voor bewerkbare slides: kies een slide, klik op een onderdeel (tekst, afbeelding, vorm), pas het aan
 * en sla op. De weergave is dezelfde als op de beamer. Wijzigingen blijven lokaal tot "Opslaan"; slidebeheer
 * (toevoegen, dupliceren, verwijderen, verplaatsen) slaat eerst openstaande wijzigingen op.
 */
export default function SlideStudioPage() {
  const { engagementId } = useParams<{ engagementId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasCapability } = useAuth();
  const canManage = hasCapability('speakers_manage');

  const [presentation, setPresentation] = useState<PresentationDetail | null>(null);
  const [slides, setSlides] = useState<PresentationSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  // Slide waarvan de opslag is geweigerd omdat iemand anders hem ondertussen heeft gewijzigd (409).
  const [conflictId, setConflictId] = useState<string | null>(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const newImageInput = useRef<HTMLInputElement>(null);
  const slidesRef = useRef<PresentationSlide[]>([]);
  slidesRef.current = slides;
  const dirtyRef = useRef<Set<string>>(dirty);
  dirtyRef.current = dirty;

  const load = useCallback(async () => {
    if (!engagementId) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      const data = await sprekersApi.getPresentation(engagementId);
      setPresentation(data);
      setSlides(data.slides);
      setDirty(new Set());
      const wanted = searchParams.get('slide');
      setCurrentId(data.slides.find((s) => s.id === wanted)?.id || data.slides[0]?.id || null);
    } catch (error) {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [engagementId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Waarschuw bij het sluiten van de pagina met niet-opgeslagen wijzigingen.
  useEffect(() => {
    if (dirty.size === 0) return undefined;
    const handler = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const current = slides.find((slide) => slide.id === currentId) || null;
  const pageHeight = current?.page_height || DEFAULT_PAGE_HEIGHT;
  const selected = useMemo(
    () => current?.elements?.find((element) => element.id === selectedId) || null, [current, selectedId],
  );

  const patchSlide = (slideId: string, patch: Partial<PresentationSlide>) => {
    setSlides((all) => all.map((slide) => (slide.id === slideId ? { ...slide, ...patch } : slide)));
    setDirty((all) => new Set(all).add(slideId));
  };

  const setElements = (elements: SlideElement[]) => { if (current) patchSlide(current.id, { elements }); };

  const replaceElement = (element: SlideElement) => {
    if (!current) return;
    setElements((current.elements || []).map((existing) => (existing.id === element.id ? element : existing)));
  };

  const addElement = (element: SlideElement) => {
    if (!current) return;
    setElements([...(current.elements || []), element]);
    setSelectedId(element.id);
  };

  const deleteElement = () => {
    if (!current || !selected) return;
    setElements((current.elements || []).filter((element) => element.id !== selected.id));
    setSelectedId(null);
  };

  const reorderElement = (direction: 'forward' | 'backward') => {
    if (!current || !selected) return;
    const list = [...(current.elements || [])];
    const index = list.findIndex((element) => element.id === selected.id);
    const target = direction === 'forward' ? index + 1 : index - 1;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    setElements(list);
  };

  const commitText = (elementId: string, value: string) => {
    const element = current?.elements?.find((e) => e.id === elementId);
    if (element) replaceElement(applyText(element, value));
  };

  const saveSlides = async (ids: string[]): Promise<boolean> => {
    let ok = true;
    for (const id of ids) {
      const slide = slidesRef.current.find((s) => s.id === id);
      if (!slide) continue;
      try {
        const saved = await sprekersApi.updateSlide(id, {
          elements: slide.elements || [], background: slide.background || null, revision: slide.revision ?? 1,
        });
        setSlides((all) => all.map((s) => (s.id === id ? { ...s, ...saved } : s)));
        setDirty((all) => { const next = new Set(all); next.delete(id); return next; });
        if (conflictId === id) setConflictId(null);
      } catch (error: any) {
        ok = false;
        if (error?.response?.status === 409) {
          // Niemand verliest hier iets: de lokale wijzigingen blijven staan tot de gebruiker kiest.
          setConflictId(id);
          setCurrentId(id);
          toast.error('Deze slide is ondertussen door iemand anders gewijzigd. Kies hoe je verder wilt.');
        } else {
          toast.error(error?.response?.status === 403
            ? 'Alleen de (mede)spreker van deze spreekbeurt kan slides opslaan.'
            : detail(error, 'Opslaan mislukt. Je wijzigingen staan nog in het scherm.'));
        }
        break;
      }
    }
    return ok;
  };

  /** Conflict: de nieuwste serverversie ophalen. Geeft de verse slide terug of null als die niet meer bestaat. */
  const fetchLatest = async (slideId: string): Promise<PresentationSlide | null> => {
    const fresh = await sprekersApi.getPresentation(engagementId as string);
    return fresh.slides.find((s) => s.id === slideId) || null;
  };

  const keepMyVersion = async () => {
    if (!conflictId) return;
    setBusy(true);
    try {
      const latest = await fetchLatest(conflictId);
      if (!latest) {
        toast.error('Deze slide bestaat niet meer. Je wijzigingen zijn niet opgeslagen.');
        return;
      }
      // Bewust overschrijven: jouw inhoud op de nieuwste revisie.
      setSlides((all) => all.map((s) => (s.id === conflictId ? { ...s, revision: latest.revision } : s)));
      slidesRef.current = slidesRef.current.map((s) => (s.id === conflictId ? { ...s, revision: latest.revision } : s));
      await saveSlides([conflictId]);
    } catch (error: any) {
      toast.error(detail(error, 'Opnieuw opslaan mislukt. Je wijzigingen staan nog in het scherm.'));
    } finally {
      setBusy(false);
    }
  };

  const loadLatestVersion = async () => {
    if (!conflictId) return;
    setBusy(true);
    try {
      const latest = await fetchLatest(conflictId);
      if (latest) {
        setSlides((all) => all.map((s) => (s.id === conflictId ? latest : s)));
      } else {
        setSlides((all) => all.filter((s) => s.id !== conflictId));
        setCurrentId(null);
      }
      setDirty((all) => { const next = new Set(all); next.delete(conflictId); return next; });
      setSelectedId(null);
      setConflictId(null);
    } catch (error: any) {
      toast.error(detail(error, 'De nieuwste versie kon niet worden geladen.'));
    } finally {
      setBusy(false);
    }
  };

  const saveAll = async (): Promise<boolean> => {
    const ids = Array.from(dirtyRef.current);
    if (ids.length === 0) return true;
    setBusy(true);
    try {
      const ok = await saveSlides(ids);
      if (ok) toast.success('Opgeslagen', { duration: 1200 });
      return ok;
    } finally {
      setBusy(false);
    }
  };

  const withSave = async (action: () => Promise<void>, failure: string) => {
    if (!(await saveAll())) return;
    setBusy(true);
    try {
      await action();
    } catch (error: any) {
      toast.error(detail(error, failure));
    } finally {
      setBusy(false);
    }
  };

  const addSlide = () => withSave(async () => {
    if (!presentation) return;
    const created = await sprekersApi.createSlide(presentation.id, {
      slide_type: 'vrije_slide', elements: [], background: current?.background || null, page_height: pageHeight,
    });
    setSlides((all) => [...all, created]);
    setCurrentId(created.id);
    setSelectedId(null);
  }, 'Slide toevoegen mislukt');

  const refreshOrder = async () => {
    const fresh = await sprekersApi.getPresentation(engagementId as string);
    setSlides(fresh.slides);
    return fresh;
  };

  const duplicateSlide = () => withSave(async () => {
    if (!current) return;
    const copy = await sprekersApi.duplicateSlide(current.id);
    await refreshOrder();
    setCurrentId(copy.id);
    setSelectedId(null);
  }, 'Dupliceren mislukt');

  const deleteSlide = () => {
    if (!current || !window.confirm('Weet je zeker dat je deze slide wilt verwijderen?')) return;
    withSave(async () => {
      const index = slides.findIndex((s) => s.id === current.id);
      await sprekersApi.deleteSlide(current.id);
      const rest = slides.filter((s) => s.id !== current.id);
      setSlides(rest);
      setCurrentId(rest[Math.min(index, rest.length - 1)]?.id || null);
      setSelectedId(null);
    }, 'Verwijderen mislukt');
  };

  const moveSlide = (direction: -1 | 1) => withSave(async () => {
    if (!presentation || !current) return;
    const ids = slides.map((s) => s.id);
    const index = ids.indexOf(current.id);
    const target = index + direction;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setSlides(await sprekersApi.reorderSlides(presentation.id, ids));
  }, 'Volgorde wijzigen mislukt');

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const media = await sprekersApi.uploadMedia(file, { speakingEngagementId: engagementId });
      return media.id;
    } catch (error: any) {
      toast.error(detail(error, 'Uploaden mislukt'));
      return null;
    }
  };

  const handlePickImage = async (purpose: 'element' | 'background', file: File) => {
    const mediaId = await uploadImage(file);
    if (!mediaId || !current) return;
    if (purpose === 'background') {
      patchSlide(current.id, { background: { ...(current.background || {}), media_id: mediaId, fit: 'cover' } });
    } else if (selected?.type === 'image') {
      replaceElement({ ...selected, media_id: mediaId });
    }
  };

  const handleAddImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const mediaId = await uploadImage(file);
    if (mediaId) addElement(newImageElement(mediaId, pageHeight));
  };

  const handleBackgroundChange = (background: SlideBackground | null) => {
    if (current) patchSlide(current.id, { background });
  };

  // Toetsenbord: verwijderen en schuiven (niet tijdens het typen in een veld).
  const handleKeyDown = (event: React.KeyboardEvent) => {
    const target = event.target as HTMLElement;
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || !selected || editingId) return;
    const step = event.shiftKey ? 10 : 1;
    const moves: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      deleteElement();
    } else if (moves[event.key]) {
      event.preventDefault();
      replaceElement(moveElement(selected, ...moves[event.key]));
    } else if (event.key === 'Enter' && selected.text) {
      event.preventDefault();
      setEditingId(selected.id);
    } else if (event.key === 'Escape') {
      setSelectedId(null);
    }
  };

  const saveAsTemplate = async () => {
    if (!presentation || !templateName.trim()) return;
    if (!(await saveAll())) return;
    setBusy(true);
    try {
      await sprekersApi.saveAsTemplate(presentation.id, { name: templateName.trim(), description: templateDescription.trim() || undefined });
      toast.success('Sjabloon opgeslagen. Je vindt het bij Presentatiesjablonen.');
      setTemplateOpen(false);
    } catch (error: any) {
      toast.error(detail(error, 'Sjabloon opslaan mislukt'));
    } finally {
      setBusy(false);
    }
  };

  const back = () => {
    if (dirty.size > 0 && !window.confirm('Er zijn niet-opgeslagen wijzigingen. Toch verlaten?')) return;
    navigate(`/sprekers/${engagementId}`);
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Zoea Studio laden...</div>;
  if (loadFailed || !presentation) {
    return (
      <div className="space-y-3 p-6">
        <p role="alert" className="text-sm text-red-600">De presentatie kon niet worden geladen.</p>
        <Button variant="outline" onClick={load}>Opnieuw proberen</Button>
        <Button variant="ghost" onClick={() => navigate(`/sprekers/${engagementId}`)}>Terug</Button>
      </div>
    );
  }
  const editableSlides = slides.filter((slide) => slide.slide_type === 'vrije_slide');
  if (slides.length === 0 || editableSlides.length === 0) {
    return (
      <div className="space-y-3 p-6">
        <p className="text-sm text-slate-600">
          Deze presentatie heeft geen bewerkbare slides. Importeer een Google Slides-presentatie als bewerkbare presentatie om hier te bewerken.
        </p>
        <Button variant="outline" onClick={() => navigate(`/sprekers/${engagementId}`)}>Terug naar de spreekbeurt</Button>
      </div>
    );
  }

  const report = presentation.source?.type === 'google_slides_editable' ? presentation.source.report : undefined;
  const currentIndex = slides.findIndex((s) => s.id === currentId);

  return (
    <div className="flex h-screen flex-col bg-slate-100" onKeyDown={handleKeyDown}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={back}><ArrowLeft className="mr-1 h-4 w-4" />Terug</Button>
          <h1 className="text-base font-semibold text-slate-900">{presentation.title || 'Presentatie'} · Zoea Studio</h1>
          {dirty.size > 0 && <span className="text-xs text-amber-700" role="status">Niet opgeslagen wijzigingen</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPreviewIndex(Math.max(0, currentIndex))}>
            <Eye className="mr-1 h-4 w-4" />Voorbeeld
          </Button>
          {canManage && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => { setTemplateName(presentation.title || ''); setTemplateOpen(true); }}>
              <BookmarkPlus className="mr-1 h-4 w-4" />Opslaan als sjabloon
            </Button>
          )}
          <Button size="sm" disabled={busy || dirty.size === 0} onClick={saveAll}>
            <Save className="mr-1 h-4 w-4" />Opslaan
          </Button>
        </div>
      </header>

      {conflictId && (
        <div role="alert" className="mx-4 mt-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <p className="font-medium">Deze slide is ondertussen door iemand anders gewijzigd.</p>
          <p className="mt-1">Je eigen wijzigingen staan nog in het scherm en zijn niet opgeslagen. Kies wat je wilt doen:</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={busy} onClick={keepMyVersion}>Mijn versie bewaren (overschrijft de andere wijziging)</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={loadLatestVersion}>Nieuwste versie laden (mijn wijzigingen vervallen)</Button>
          </div>
        </div>
      )}

      {report && report.items.length > 0 && <div className="px-4 pt-2"><ConversionReportNotice report={report} /></div>}

      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <aside className="flex max-h-40 shrink-0 gap-2 overflow-auto border-b border-slate-200 bg-white p-2 md:max-h-none md:w-52 md:flex-col md:border-b-0 md:border-r" aria-label="Slides">
          {slides.map((slide, index) => (
            <button
              key={slide.id} type="button" onClick={() => { setCurrentId(slide.id); setSelectedId(null); setEditingId(null); }}
              aria-label={`Slide ${index + 1}`} aria-current={slide.id === currentId}
              className={`w-32 shrink-0 rounded-md border-2 p-0.5 text-left md:w-full ${slide.id === currentId ? 'border-teal-500' : 'border-transparent'}`}
            >
              <LazySlidePreview slide={slide} />
              <span className="block px-1 text-xs text-slate-500">{index + 1}{dirty.has(slide.id) ? ' •' : ''}</span>
            </button>
          ))}
          <div className="flex shrink-0 gap-1 md:flex-wrap">
            <Button variant="outline" size="sm" disabled={busy} onClick={addSlide} aria-label="Slide toevoegen"><Plus className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" disabled={busy || !current} onClick={duplicateSlide} aria-label="Slide dupliceren"><Copy className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" disabled={busy || currentIndex <= 0} onClick={() => moveSlide(-1)} aria-label="Slide omhoog"><ArrowUp className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" disabled={busy || currentIndex < 0 || currentIndex >= slides.length - 1} onClick={() => moveSlide(1)} aria-label="Slide omlaag"><ArrowDown className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" disabled={busy || !current} onClick={deleteSlide} aria-label="Slide verwijderen"><Trash2 className="h-4 w-4" /></Button>
          </div>
        </aside>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap gap-2 border-b border-slate-200 bg-white px-3 py-2" role="toolbar" aria-label="Onderdelen toevoegen">
            <Button variant="outline" size="sm" disabled={!current} onClick={() => addElement(newTextElement(pageHeight))}><Type className="mr-1 h-4 w-4" />Tekst</Button>
            <Button variant="outline" size="sm" disabled={!current} onClick={() => addElement(newShapeElement('rect', pageHeight))}><Square className="mr-1 h-4 w-4" />Rechthoek</Button>
            <Button variant="outline" size="sm" disabled={!current} onClick={() => addElement(newShapeElement('ellipse', pageHeight))}><Circle className="mr-1 h-4 w-4" />Ellips</Button>
            <Button variant="outline" size="sm" disabled={!current} onClick={() => newImageInput.current?.click()}><ImagePlus className="mr-1 h-4 w-4" />Afbeelding</Button>
            <input ref={newImageInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" aria-label="Nieuwe afbeelding" onChange={handleAddImage} />
          </div>
          <div className="min-h-0 flex-1 p-4">
            {current && (
              <div className="h-full w-full" data-testid="studio-canvas">
                <SlideCanvasEditor
                  visual={current} selectedId={selectedId} onSelect={setSelectedId} onElementChange={replaceElement}
                  editingId={editingId} onEditingChange={setEditingId} onCommitText={commitText}
                />
              </div>
            )}
          </div>
        </main>

        <aside className="max-h-72 shrink-0 overflow-auto border-t border-slate-200 bg-white p-3 md:max-h-none md:w-72 md:border-l md:border-t-0">
          <ElementInspector
            element={selected} background={current?.background} disabled={busy} onChange={replaceElement}
            onDelete={deleteElement} onReorder={reorderElement} onBackgroundChange={handleBackgroundChange} onPickImage={handlePickImage}
          />
        </aside>
      </div>

      <SlidePreviewDialog slides={slides} index={previewIndex} onIndexChange={setPreviewIndex} />

      <Dialog open={templateOpen} onOpenChange={(open: boolean) => { if (!busy) setTemplateOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Opslaan als sjabloon</DialogTitle>
            <DialogDescription>
              Je organisatie kan dit sjabloon gebruiken voor nieuwe presentaties. Slides, tekst, vormen en afbeeldingen worden gekopieerd;
              spreeknotities en preekinhoud gaan nooit mee.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="template-name">Naam</Label>
              <Input id="template-name" value={templateName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateName(e.target.value)} maxLength={200} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="template-description">Beschrijving (optioneel)</Label>
              <Input id="template-description" value={templateDescription} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateDescription(e.target.value)} maxLength={1000} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setTemplateOpen(false)}>Annuleren</Button>
            <Button disabled={busy || !templateName.trim()} onClick={saveAsTemplate}>{busy ? 'Bezig...' : 'Sjabloon opslaan'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
